import { useEffect, useMemo, useState } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiDollarSign,
  FiCreditCard,
  FiFilter,
  FiUpload,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  TableActions,
  BankAccountForm,
  Select,
  MultiSelect,
  OfxImportModal,
} from '../../components';
import { usePermission } from '../../hooks/usePermission';
import { BANK_OPTIONS } from '../../constants/banks';

export default function BankAccountManager() {
  const permissions = usePermission('financial');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importAccount, setImportAccount] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  
  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadAccounts(selectedCompanyFilter || 'all');
  }, [selectedCompanyFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompanyFilter]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Erro ao carregar empresas.');
    }
  }

  async function loadAccounts(companyId) {
    try {
      setLoading(true);
      const data = await Api.getAccounts(companyId);

      if (Array.isArray(data)) {
        setAccounts(data);
        setHasLoadedOnce(true);
      } else {
        setError('Não foi possível carregar as contas bancárias.');
      }
    } catch (err) {
      setError('Erro ao carregar contas bancárias. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setSelectedAccount(null);
    setIsModalOpen(true);
  }

  function handleEdit(account) {
    setSelectedAccount(account);
    setIsModalOpen(true);
  }

  function handleImport(account) {
    setImportAccount(account);
    setIsImportModalOpen(true);
  }

  function handleFormSuccess() {
    loadAccounts(selectedCompanyFilter || 'all');
    setIsModalOpen(false);
  }

  function confirmDelete(account) {
    setAccountToDelete(account);
    setIsDeleteModalOpen(true);
  }

  async function handleDelete() {
    if (!accountToDelete) return;

    try {
      setIsDeleting(true);
      // Use selected filter or 'all' context for deletion, as account.company_id might be null for multi-company accounts
      const targetCompanyId = selectedCompanyFilter || 'all';
      await Api.deleteAccount(targetCompanyId, accountToDelete.id);
      toast.success('Conta excluída com sucesso!');
      loadAccounts(selectedCompanyFilter || 'all');
      setIsDeleteModalOpen(false);
      setAccountToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir conta bancária.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyFilter(idsWithoutAll[0]);
    } else {
      setSelectedCompanyFilter('');
    }
  }

  const filteredAccounts = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return accounts.filter((account) => {
      const searchLower = searchTerm.toLowerCase();
      const bankOption = BANK_OPTIONS.find(b => b.value === account.bank);
      let bankLabel = bankOption ? bankOption.label : account.bank;
      
      if (account.type === 'cash') {
        bankLabel = 'Conta Caixinha';
      }

      const matchesSearch =
        account.name.toLowerCase().includes(searchLower) ||
        (bankLabel && bankLabel.toLowerCase().includes(searchLower));

      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        (account.companies && account.companies.some(c => specificIds.includes(String(c.id)))) ||
        (account.company_id && specificIds.includes(String(account.company_id)));
      
      return matchesSearch && matchesCompany;
    });
  }, [accounts, searchTerm, companyFilterValues]);

  const totalItems = filteredAccounts.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedAccounts = filteredAccounts.slice(
    startIndex,
    startIndex + pageSize,
  );

  const getIcon = (type) => {
    switch (type) {
      case 'checking': return <FiCreditCard size={18} />;
      case 'cash': return <FiDollarSign size={18} />;
      default: return <FiCreditCard size={18} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'checking': return 'Conta Corrente';
      case 'cash': return 'Conta Caixinha';
      default: return 'Outras Contas';
    }
  };

  const columns = [
    {
      id: 'name',
      header: 'Nome',
      width: '2fr',
      cellClassName: 'manager-table-cell-primary',
      render: (account) => (
        <>
          <div className="manager-table-avatar">
            {getIcon(account.type)}
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{account.name}</span>
            <span className="manager-table-subtitle">{getTypeLabel(account.type)}</span>
          </div>
        </>
      )
    },
    {
      id: 'bank',
      header: 'Banco',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (account) => {
        const bankOption = BANK_OPTIONS.find(b => b.value === account.bank);
        return (
          <span className="manager-table-text">
            {account.type === 'cash' ? 'Conta Caixinha' : (bankOption ? bankOption.label : account.bank || '-')}
          </span>
        );
      }
    },
    {
      id: 'balance',
      header: 'Saldo',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (account) => (
        <span style={{ 
          fontWeight: 600, 
          color: parseFloat(account.balance) >= 0 ? '#16a34a' : '#dc2626' 
        }}>
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(account.balance)}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: (account) => (
        <span className={`status-badge ${account.active ? 'active' : 'inactive'}`}>
          {account.active ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
  {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (account) => (
        <span className="manager-table-text">
          {account.companies && account.companies.length > 0 
            ? account.companies.map(c => c.trade_name || c.corporate_name).join(', ')
            : (account.company?.trade_name || account.company?.corporate_name || '-')}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: (account) => (
        <TableActions>
          {permissions.canCreate && (
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleImport(account);
              }}
            >
              <FiUpload size={16} />
              Importar
            </button>
          )}
          {permissions.canEdit && (
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(account);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button 
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                confirmDelete(account);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  function BankAccountSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiCreditCard size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <BankAccountSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiCreditCard size={22} />
                </div>
                <div>
                  <h1>Contas Bancárias</h1>
                  <p>Gerencie suas contas e caixinhas</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Nova Conta
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {accounts.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredAccounts.length} de {accounts.length} registros
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div style={{ flex: 1 }}>
                  <MultiSelect
                    id="company-filter"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({ 
                        value: String(c.id), 
                        label: c.trade_name || c.corporate_name 
                      }))
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    placeholder="Filtrar por empresa..."
                    searchable
                    label={"Empresa"}
                  />
                </div>

                <div style={{ flex: 1 }}>
                   <Input
                    id="search"
                    label="Buscar"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Nome da conta..."
                    icon={FiSearch}
                  />
                </div>
                <Select
                  id="type-filter"
                  label="Tipo"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'Todos os tipos' },
                    { value: 'checking', label: 'Conta Corrente' },
                    { value: 'cash', label: 'Conta Caixinha' },
                    { value: 'other', label: 'Outras' },
                  ]}
                />
              </div>
            </Card>

            {error ? (
              <div className="manager-error-container">
                <p>{error}</p>
                <Button onClick={() => loadAccounts(selectedCompanyFilter || 'all')}>
                  Tentar Novamente
                </Button>
              </div>
            ) : (
              <Table
                items={paginatedAccounts}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                getRowKey={(account) => account.id}
                emptyMessage="Nenhuma conta bancária encontrada."
              />
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <Modal
          title={selectedAccount ? 'Editar Conta' : 'Nova Conta Bancária'}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          maxWidth="800px"
          closeOnOverlayClick={false}
        >
          <BankAccountForm
            initialData={selectedAccount}
            companies={companies}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalOpen(false)}
            companyId={selectedCompanyFilter}
          />
        </Modal>
      )}

      {isDeleteModalOpen && (
        <Modal
          title="Confirmar Exclusão"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja excluir a conta{' '}
              <span className="delete-modal-company-name">
                {accountToDelete?.name}
              </span>
              ?
            </p>
            <p>Essa ação não poderá ser desfeita e pode afetar lançamentos vinculados.</p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>
      )}
      {isImportModalOpen && (
        <OfxImportModal 
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={() => {
            setIsImportModalOpen(false);
            loadAccounts(selectedCompanyFilter || 'all');
          }}
          account={importAccount}
          companyId={importAccount?.company_id || selectedCompanyFilter}
          companies={companies}
        />
      )}
    </PageComponent>
  );
}
